import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTemplate, getTemplates, testTemplate, updateTemplate } from 'API/engagement';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EngagementTemplate, TemplateChannelText, TemplateVariable, TopicClass } from 'types/engagement.types';
import { fmtDate } from '../../shared/format';
import { QueryState, SectionCard } from '../../shared/ui';
import { templateStatus, templateTested, templateValidation } from '../engagement-logic';

type Lang = 'de' | 'en' | 'fr';
type TplChannel = 'notification' | 'email';

const emptyTemplate = (): EngagementTemplate => ({
  key: '',
  name: '',
  topicClass: 'product',
  system: false,
  active: false,
  variants: { de: { notification: { title: '', body: '', ctaLabel: '' } } },
  variables: [],
  ctaUrl: '',
  recipientBenefit: '',
  lastTestedAt: null,
});

const TemplateEditor: React.FC<{ initial: EngagementTemplate; isNew: boolean; onDone(): void }> = ({ initial, isNew, onDone }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [tpl, setTpl] = useState<EngagementTemplate>(initial);
  const [lang, setLang] = useState<Lang>('de');
  const [channel, setChannel] = useState<TplChannel>('notification');
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const text: TemplateChannelText = tpl.variants?.[lang]?.[channel] ?? { body: '' };
  const setText = (patch: Partial<TemplateChannelText>) =>
    setTpl((prev) => ({
      ...prev,
      variants: { ...prev.variants, [lang]: { ...(prev.variants?.[lang] ?? {}), [channel]: { ...text, ...patch } } },
    }));

  const setVar = (i: number, patch: Partial<TemplateVariable>) =>
    setTpl((prev) => ({ ...prev, variables: prev.variables.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));

  const save = useMutation({
    mutationFn: () => (isNew ? createTemplate(tpl) : updateTemplate(tpl.key, tpl)),
    onSuccess: (saved) => {
      // The save answer has no testedCurrentWording: a changed wording needs a new test, an unchanged one keeps its state.
      const wording = (x: EngagementTemplate) => JSON.stringify([x.variants, x.ctaUrl ?? '', x.variables]);
      if (saved) setTpl({ ...saved, testedCurrentWording: wording(saved) === wording(initial) ? templateTested(initial) : false });
      setMessage({ severity: 'success', text: t('engTemplateSaved') });
      queryClient.invalidateQueries({ queryKey: ['engagement', 'templates'] });
    },
    onError: (e) => setMessage({ severity: 'error', text: `${t('engComposeErrorGeneric')} (${(e as Error).message})` }),
  });

  const test = useMutation({
    mutationFn: () => testTemplate(tpl.key),
    onSuccess: () => {
      setTpl((prev) => ({ ...prev, lastTestedAt: new Date().toISOString(), testedCurrentWording: true }));
      setMessage({ severity: 'success', text: t('engTemplateTestSent') });
      queryClient.invalidateQueries({ queryKey: ['engagement', 'templates'] });
    },
    onError: (e) => setMessage({ severity: 'error', text: `${t('engComposeErrorGeneric')} (${(e as Error).message})` }),
  });

  const missing = templateValidation(tpl);

  return (
    <SectionCard
      title={isNew ? t('engTemplateNew') : t('engTemplateEditTitle', { name: tpl.name, lang: lang.toUpperCase(), channel: t(`engChannel_${channel}`) })}
      action={
        <Button size={'small'} onClick={onDone}>
          {t('engClose')}
        </Button>
      }
    >
      <Stack gap={2}>
        <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
          <TextField size={'small'} label={t('engTemplateKey')} value={tpl.key} disabled={!isNew} onChange={(e) => setTpl({ ...tpl, key: e.target.value })} />
          <TextField size={'small'} label={t('engTemplateName')} value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} />
          <TextField
            select
            size={'small'}
            label={t('engComposeTopicClass')}
            value={tpl.topicClass}
            disabled={tpl.system}
            onChange={(e) => setTpl({ ...tpl, topicClass: e.target.value as TopicClass })}
            sx={{ minWidth: 160 }}
          >
            {(['transactional', 'product', 'offer', 'news'] as TopicClass[]).map((c) => (
              <MenuItem key={c} value={c}>
                {t(`engTopicClass_${c}`)}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={!templateTested(tpl) ? t('engTemplateActivateNeedsTest') : ''}>
            <span>
              <FormControlLabel
                control={
                  <Switch
                    checked={tpl.active}
                    disabled={!templateTested(tpl) && !tpl.active}
                    onChange={(e) => setTpl({ ...tpl, active: e.target.checked })}
                  />
                }
                label={t('engTemplateActive')}
              />
            </span>
          </Tooltip>
        </Stack>

        <Stack direction={'row'} gap={2} flexWrap={'wrap'}>
          <ToggleButtonGroup size={'small'} exclusive value={lang} onChange={(_, v) => v && setLang(v)}>
            {(['de', 'en', 'fr'] as Lang[]).map((l) => (
              <ToggleButton key={l} value={l}>
                {l.toUpperCase()}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup size={'small'} exclusive value={channel} onChange={(_, v) => v && setChannel(v)}>
            <ToggleButton value={'notification'}>{t('engChannel_notification')}</ToggleButton>
            <ToggleButton value={'email'}>{t('engChannel_email')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {channel === 'notification' ? (
          <TextField size={'small'} label={t('engTemplateTitle')} value={text.title ?? ''} onChange={(e) => setText({ title: e.target.value })} />
        ) : (
          <TextField size={'small'} label={t('engTemplateSubject')} value={text.subject ?? ''} onChange={(e) => setText({ subject: e.target.value })} />
        )}
        <TextField size={'small'} multiline minRows={4} label={t('engTemplateBody')} value={text.body ?? ''} onChange={(e) => setText({ body: e.target.value })} />
        <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
          {tpl.variables.map((v) => (
            <Chip key={v.name} size={'small'} label={`{{${v.name}}}`} onClick={() => setText({ body: `${text.body ?? ''}{{${v.name}}}` })} />
          ))}
        </Stack>
        <Stack direction={'row'} gap={1.5}>
          <TextField size={'small'} label={t('engComposeCtaLabel')} value={text.ctaLabel ?? ''} onChange={(e) => setText({ ctaLabel: e.target.value })} />
          <TextField size={'small'} fullWidth label={t('engComposeCtaUrl')} value={tpl.ctaUrl ?? ''} onChange={(e) => setTpl({ ...tpl, ctaUrl: e.target.value })} />
        </Stack>
        <TextField
          size={'small'}
          required
          label={t('engTemplateBenefit')}
          helperText={t('engTemplateBenefitHelp')}
          error={!tpl.recipientBenefit.trim()}
          value={tpl.recipientBenefit}
          onChange={(e) => setTpl({ ...tpl, recipientBenefit: e.target.value })}
        />

        <Box>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('engTemplateVariables')}</Typography>
            <Button size={'small'} startIcon={<AddIcon />} onClick={() => setTpl({ ...tpl, variables: [...tpl.variables, { name: '', source: 'deterministic' }] })}>
              {t('engTemplateAddVariable')}
            </Button>
          </Stack>
          <Table size={'small'}>
            <TableHead>
              <TableRow>
                <TableCell>{t('engTemplateVarName')}</TableCell>
                <TableCell>{t('engTemplateVarSource')}</TableCell>
                <TableCell>{t('engTemplateVarDetail')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tpl.variables.map((v, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField size={'small'} value={v.name} onChange={(e) => setVar(i, { name: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField select size={'small'} value={v.source} onChange={(e) => setVar(i, { source: e.target.value as TemplateVariable['source'] })}>
                      {(['deterministic', 'manual', 'llm'] as const).map((src) => (
                        <MenuItem key={src} value={src}>
                          {t(`engVarSource_${src}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {v.source === 'deterministic' ? (
                      <TextField size={'small'} fullWidth placeholder={'inbox.unansweredCount'} value={v.path ?? ''} onChange={(e) => setVar(i, { path: e.target.value })} />
                    ) : v.source === 'llm' ? (
                      <TextField size={'small'} fullWidth placeholder={t('engVarLlmPlaceholder')} value={v.instruction ?? ''} onChange={(e) => setVar(i, { instruction: e.target.value })} />
                    ) : (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('engVarManualHint')}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton size={'small'} onClick={() => setTpl({ ...tpl, variables: tpl.variables.filter((_, j) => j !== i) })}>
                      <DeleteOutlineIcon fontSize={'small'} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{t('engTemplateVarsNote')}</Typography>
        </Box>

        <Stack direction={'row'} gap={1.5} alignItems={'center'}>
          <Button variant={'contained'} disabled={missing.length > 0 || save.isPending} onClick={() => save.mutate()}>
            {t('engSave')}
          </Button>
          <Button variant={'outlined'} disabled={isNew || test.isPending} onClick={() => test.mutate()}>
            {t('engComposeTest')}
          </Button>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {tpl.lastTestedAt ? t('engTemplateLastTested', { at: fmtDate(tpl.lastTestedAt, i18n.language, true) }) : t('engTemplateNeverTested')}
          </Typography>
        </Stack>
        {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
      </Stack>
    </SectionCard>
  );
};

const TemplatesTab: React.FC = () => {
  const { t } = useTranslation();
  const templates = useQuery({ queryKey: ['engagement', 'templates'], queryFn: getTemplates });
  const [editing, setEditing] = useState<{ tpl: EngagementTemplate; isNew: boolean } | null>(null);

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
      <Box sx={{ flex: 2, minWidth: 0 }}>
        <SectionCard
          title={t('engTemplatesTitle')}
          action={
            <Button size={'small'} startIcon={<AddIcon />} onClick={() => setEditing({ tpl: emptyTemplate(), isNew: true })}>
              {t('engTemplateNew')}
            </Button>
          }
        >
          <QueryState isLoading={templates.isLoading} error={templates.error} isEmpty={(templates.data ?? []).length === 0} emptyText={t('engTemplatesEmpty')}>
            <Table size={'small'}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('engTemplateName')}</TableCell>
                  <TableCell>{t('engComposeTopicClass')}</TableCell>
                  <TableCell>{t('engColStatus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(templates.data ?? []).map((tpl) => {
                  const status = templateStatus(tpl);
                  return (
                    <TableRow key={tpl.key} hover sx={{ cursor: 'pointer' }} selected={editing?.tpl.key === tpl.key} onClick={() => setEditing({ tpl, isNew: false })}>
                      <TableCell>
                        {tpl.name}
                        {tpl.system ? <Chip size={'small'} label={t('engTemplateSystem')} sx={{ ml: 1 }} /> : null}
                      </TableCell>
                      <TableCell>{t(`engTopicClass_${tpl.topicClass}`)}</TableCell>
                      <TableCell>
                        <Chip
                          size={'small'}
                          label={t(`engTemplateStatus_${status}`)}
                          color={status === 'active' ? 'success' : status === 'untested' ? 'warning' : 'default'}
                          variant={status === 'active' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </QueryState>
        </SectionCard>
      </Box>
      <Box sx={{ flex: 3, minWidth: 0 }}>
        {editing ? (
          <TemplateEditor key={editing.isNew ? '__new' : editing.tpl.key} initial={editing.tpl} isNew={editing.isNew} onDone={() => setEditing(null)} />
        ) : (
          <SectionCard>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engTemplatePick')}</Typography>
          </SectionCard>
        )}
      </Box>
    </Stack>
  );
};

export default TemplatesTab;
